import React from "react";
import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import {
  Gauge as GaugeHigh, Stack, Tag, UsersThree, Scroll as ScrollText, PaintBrushBroad,
  ArrowSquareOut, SignOut, ShieldCheck, Users, Gear, Coins, Car, ForkKnife, Truck, Briefcase, VideoCamera, PaperPlaneTilt, Phone, Sparkle, Heart, Robot, Storefront, ChartLineUp, Target,
} from "@phosphor-icons/react";

const nav = [
  { to: "/command", label: "Command Center", icon: GaugeHigh, end: true },
  { to: "/command/ai-sales", label: "SI Sales", icon: Sparkle },
  { to: "/command/ai-sales/catalog", label: "Sales Catalog", icon: Storefront },
  { to: "/command/ai-sales/revenue", label: "Revenue & Analytics", icon: ChartLineUp },
  { to: "/command/ai-sales/packages", label: "Packages & Pricing", icon: Tag },
  { to: "/command/ai-sales/knowledge", label: "How-To Knowledge", icon: ScrollText },
  { to: "/command/ai-sales/closing", label: "Closing Config", icon: Target },
  { to: "/command/ai-sales/automation", label: "Sales Automation", icon: Robot },
  { to: "/command/ai-sales/journeys", label: "Member Journeys", icon: Robot },
  { to: "/command/users", label: "Customers", icon: Users },
  { to: "/command/leads", label: "Leads", icon: Target },
  { to: "/command/pbx", label: "Cloud PBX", icon: Phone },
  { to: "/command/numbers", label: "Numbers", icon: Phone },
  { to: "/command/telecom-rates", label: "Telecom Rates", icon: Coins },
  { to: "/command/telecom-orders", label: "Hardware Orders", icon: Storefront },
  { to: "/command/shop", label: "Shop Manager", icon: Storefront },
  { to: "/command/plans", label: "Plans (Mobile & Landline)", icon: Phone },
  { to: "/command/live-connect", label: "Live Connect", icon: VideoCamera },
  { to: "/command/providers", label: "Providers", icon: Stack },
  { to: "/command/catalog", label: "Products & Pricing", icon: Tag },
  { to: "/command/rates", label: "Calling Rates", icon: Coins },
  { to: "/command/topup-margins", label: "Top-Up Margins", icon: Coins },
  { to: "/command/pricing", label: "Gift Card & Airtime Pricing", icon: Coins },
  { to: "/command/payouts", label: "Payouts", icon: Coins },
  { to: "/command/rides", label: "iYeneh Ride", icon: Car },
  { to: "/command/eat", label: "iYeneh Eat", icon: ForkKnife },
  { to: "/command/fleet", label: "iYeneh Fleet", icon: Truck },
  { to: "/command/freelance", label: "iYeneh Freelance", icon: Briefcase },
  { to: "/command/meet", label: "Meet Settings", icon: VideoCamera },
  { to: "/command/organizations", label: "Organizations", icon: UsersThree },
  { to: "/command/audit", label: "Audit Logs", icon: ScrollText },
  { to: "/command/brand", label: "Brand Settings", icon: PaintBrushBroad },
  { to: "/command/settings", label: "Platform Settings", icon: Gear },
  { to: "/command/messaging", label: "Messaging", icon: PaperPlaneTilt },
  { to: "/command/esim-review", label: "eSIM Review", icon: Stack },
  { to: "/command/match-packages", label: "iYeneh Match", icon: Heart },
];

export function CommandCenterLayout({ children }) {
  const { user, logout } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();

  return (
    <div className="command min-h-screen bg-background text-foreground">
        {/* sidebar */}
        <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col border-r border-white/10 bg-card md:flex">
          <Link to="/" data-testid="cc-brand-home" aria-label={`${brand.brand_name} — Home`} title={`${brand.brand_name} — Home`} className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
            <img src="/icons/icon-512.png" alt="iYeneh Connect" className="h-7 w-7" />
            <span className="text-base font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>iYeneh Connect</span>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Command Center</p>
          </Link>
          <div className="border-b border-white/10 p-3">
            <button data-testid="cc-dashboard-top" onClick={() => navigate("/app")}
              className="flex w-full items-center gap-2 rounded-lg bg-primary/15 px-3 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/25">
              <ArrowSquareOut size={16} weight="bold" /> My Dashboard
            </button>
          </div>
          <nav className="flex-1 space-y-1 overflow-y-auto p-3">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} data-testid={`cc-nav-${n.label.toLowerCase().split(" ")[0]}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-white/5 hover:text-foreground"
                  }`}>
                <n.icon size={18} weight="bold" /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="border-t border-white/10 p-3">
            <button data-testid="exit-to-app" onClick={() => navigate("/app")}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-white/5 hover:text-foreground">
              <ArrowSquareOut size={16} /> Customer App
            </button>
            <button data-testid="cc-logout" onClick={logout}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-destructive hover:bg-white/5">
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </aside>

        <div className="min-w-0 md:ml-64">
          {/* mobile top bar */}
          <header className="flex items-center justify-between border-b border-white/10 bg-card px-4 py-3 md:hidden">
            <span className="font-display font-semibold">Command Center</span>
            <button data-testid="cc-dashboard-mobile" onClick={() => navigate("/app")} className="flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1.5 text-xs font-semibold text-primary">
              <ArrowSquareOut size={13} weight="bold" /> My Dashboard
            </button>
          </header>
          {/* mobile nav row */}
          <div className="no-scrollbar flex gap-2 overflow-x-auto border-b border-white/10 bg-card px-4 py-2 md:hidden">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                    isActive ? "bg-primary text-primary-foreground" : "bg-white/5 text-muted-foreground"
                  }`}>{n.label}</NavLink>
            ))}
          </div>
          <main className="min-w-0 p-4 md:p-8">{children}</main>
        </div>
    </div>
  );
}
